export default {
  index: {
    title: 'Guides des marées et des courants',
    description:
      'Comprenez le fonctionnement des marées et des courants de marée : coefficients de marée, courants de flot et de jusant, références de marée (zéros), et comment sont calculées les prédictions de marée.',
    subtitle:
      'De courtes explications en langage clair sur la science qui sous-tend les prédictions de marées et de courants.',
  },
  back: 'Retour à tous les guides',
  guides: {
    'tidal-coefficients': {
      title: 'Les coefficients de marée expliqués',
      description:
        "Ce que signifient les coefficients de marée : l'échelle de 20 à 120, le lien entre les marées de vive-eau et de morte-eau et la Lune, et pourquoi un coefficient plus élevé produit un plus grand marnage.",
      blurb:
        "L'échelle de 20 à 120 qui résume l'ampleur d'une marée et la manière dont la Lune la commande.",
      body: [
        {
          p: "Un coefficient de marée est un nombre unique qui résume l'ampleur d'une marée un jour donné. Il est largement utilisé en France et dans d'autres pays qui suivent la tradition hydrographique française. Le coefficient est sans dimension et s'échelonne normalement d'environ 20 à 120 : plus le nombre est élevé, plus le marnage entre la pleine mer et la basse mer est grand, et plus les courants de marée associés sont forts.",
        },
        { h2: "L'échelle de 20 à 120" },
        {
          p: "Le coefficient compare le marnage du jour à une référence à long terme. Plusieurs valeurs de l'échelle ont une signification conventionnelle :",
        },
        {
          list: [
            {
              term: '20',
              text: 'les plus petites marées, proches du marnage astronomique minimal.',
            },
            { term: '45', text: 'une marée de morte-eau moyenne, lorsque le marnage est faible.' },
            {
              term: '70',
              text: 'une marée moyenne, à peu près à mi-chemin entre les mortes-eaux et les vives-eaux.',
            },
            { term: '95', text: 'une marée de vive-eau moyenne, lorsque le marnage est grand.' },
            {
              term: '120',
              text: 'les plus grandes marées, proches du marnage astronomique maximal.',
            },
          ],
        },
        { h2: 'Pourquoi il varie : la Lune' },
        {
          p: "Les coefficients montent et descendent selon le cycle vives-eaux/mortes-eaux d'environ deux semaines. Les marées de vive-eau, à coefficients élevés, surviennent un jour ou deux après chaque nouvelle et pleine Lune, lorsque le Soleil et la Lune s'alignent et que leurs forces de marée s'additionnent. Les marées de morte-eau, à coefficients faibles, surviennent près des premier et dernier quartiers, lorsque le Soleil et la Lune sont à angle droit et se compensent en partie.",
        },
        {
          p: "La distance de la Lune compte aussi. Lorsqu'une marée de vive-eau coïncide avec le périgée (le point où la Lune est au plus près de la Terre), le coefficient atteint ses valeurs les plus élevées. Les plus grandes marées ont tendance à se regrouper près des équinoxes de printemps et d'automne.",
        },
        { h2: "Ce qu'un coefficient ne vous dit pas" },
        {
          p: "Le coefficient ne décrit que le marnage astronomique. Il n'inclut pas l'effet de la météo : le vent et la pression atmosphérique peuvent élever ou abaisser le niveau réel de l'eau indépendamment du coefficient. C'est aussi une propriété de la marée dans son ensemble pour ce jour-là, et non une hauteur en mètres ou en pieds en un lieu particulier.",
        },
      ],
    },
    'tidal-currents': {
      title: 'Flot, jusant et étale : comment fonctionnent les courants de marée',
      description:
        "Comment fonctionnent les courants de marée : la direction du flot et du jusant, l'étale au moment du renversement, la vitesse du courant en nœuds et sa direction (le set), et pourquoi l'étale ne coïncide pas toujours avec la pleine et la basse mer.",
      blurb:
        "Le flot, le jusant et l'étale, et pourquoi le courant le plus fort n'est pas toujours à pleine mer.",
      body: [
        {
          p: "Les courants de marée sont le mouvement horizontal de l'eau qui accompagne la montée et la descente verticales de la marée. Là où la hauteur de la marée indique la profondeur de l'eau, le courant indique dans quelle direction elle s'écoule et à quelle vitesse. Les courants comptent surtout dans les chenaux, les passes et les estuaires, où ils peuvent atteindre plusieurs nœuds.",
        },
        { h2: 'Flot, jusant et étale' },
        {
          list: [
            {
              term: 'Courant de flot',
              text: "eau s'écoulant vers la côte ou remontant un estuaire, généralement pendant que la marée monte.",
            },
            {
              term: 'Courant de jusant',
              text: "eau s'écoulant vers le large ou descendant un estuaire, généralement pendant que la marée descend.",
            },
            {
              term: 'Étale',
              text: "la brève pause où le courant est proche de zéro lorsqu'il se renverse entre le flot et le jusant.",
            },
          ],
        },
        {
          p: "La vitesse du courant se mesure en nœuds (milles nautiques par heure). Sa direction, appelée le set, est donnée comme un relèvement au compas : la direction vers laquelle l'eau s'écoule.",
        },
        { h2: "L'étale n'est pas toujours à pleine mer" },
        {
          p: "On suppose souvent que le courant s'arrête à la pleine et à la basse mer. Ce n'est vrai qu'en certains endroits. Le moment dépend du comportement local de l'onde de marée. Là où la marée se comporte comme une onde progressive, comme dans un long chenal ouvert, le flot et le jusant les plus forts se produisent près de la pleine et de la basse mer, et l'étale se situe près du milieu. Là où elle se comporte comme une onde stationnaire, comme au fond d'une baie fermée, c'est l'inverse : le courant le plus rapide se produit à mi-marée et l'étale survient près de la pleine et de la basse mer. Les tables de courants locales sont le guide fiable.",
        },
        { h2: 'Courants alternatifs et giratoires' },
        {
          p: "Dans les chenaux étroits, le courant est généralement alternatif : il s'écoule dans un sens au flot, puis se renverse et revient au jusant. En eau côtière ouverte, le courant est souvent giratoire : il change de direction continuellement tout autour du compas au cours d'un cycle de marée, au lieu de s'inverser le long d'une seule ligne.",
        },
      ],
    },
    'tidal-datums': {
      title: 'Références de marée et hauteurs de marée',
      description:
        'À partir de quoi se mesure une hauteur de marée : le zéro hydrographique, le plus bas niveau astronomique (LAT), le niveau moyen des basses mers inférieures (MLLW), et pourquoi les références diffèrent selon les pays.',
      blurb:
        "Le zéro hydrographique, LAT, MLLW, et ce que le chiffre d'une hauteur de marée signifie réellement.",
      body: [
        {
          p: "Toute hauteur de marée est une mesure au-dessus d'un niveau de référence appelé référence ou zéro. Lorsqu'une table des marées indique que l'eau atteindra 1.8 mètre, cela signifie 1.8 mètre au-dessus de la référence de marée locale, et non au-dessus du fond ni au-dessus du niveau de la mer au sens courant. Connaître la référence est ce qui donne un sens à une hauteur.",
        },
        { h2: "Ce qu'est une référence de marée" },
        {
          p: "Une référence de marée est une altitude de référence définie par un stade particulier de la marée, moyennée sur une longue période : par convention environ 19 ans, ce qui lisse le cycle de 18.6 ans de l'orbite de la Lune. Comme il s'agit d'une moyenne d'observations réelles, une référence est propre à un lieu et à l'organisme qui l'a calculée.",
        },
        { h2: 'Le zéro hydrographique et les références courantes' },
        {
          p: 'Les cartes marines utilisent une référence de basse mer afin que la profondeur réelle soit presque toujours au moins égale à la profondeur portée sur la carte. Ce zéro hydrographique est le zéro à partir duquel se comptent à la fois les profondeurs cartographiées et les hauteurs de marée. Différents pays adoptent des références différentes :',
        },
        {
          list: [
            {
              term: 'LAT (plus basse mer astronomique)',
              text: 'le niveau le plus bas prévisible par météo moyenne ; utilisé par le Royaume-Uni et de nombreux autres pays.',
            },
            {
              term: 'MLLW (niveau moyen des basses mers inférieures)',
              text: 'la moyenne de la plus basse des deux basses mers de chaque jour ; le zéro hydrographique standard aux États-Unis.',
            },
            {
              term: 'MSL (niveau moyen de la mer)',
              text: "le niveau moyen de l'eau à long terme, utilisé principalement comme référence pour les altitudes terrestres plutôt que pour les cartes.",
            },
          ],
        },
        {
          p: "Les références de pleine mer, comme le niveau moyen des pleines mers (MHW) et le niveau moyen des pleines mers supérieures (MHHW), servent au but inverse, par exemple pour définir un trait de côte ou le tirant d'air d'un pont.",
        },
        { h2: 'Pourquoi les hauteurs ne sont pas directement comparables' },
        {
          p: "Comme les références diffèrent, une hauteur à une station ne peut être comparée directement à une hauteur à une autre que si toutes deux utilisent la même référence. Une basse mer de 0.5 mètre mesurée à partir de LAT n'est pas le même niveau d'eau qu'une basse mer de 0.5 mètre mesurée à partir de MLLW. C'est aussi pourquoi une même marée physique peut être décrite par des chiffres différents dans les tables de différents pays.",
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Comment sont calculées les prédictions de marée',
      description:
        "Comment les marées sont prédites : le forçage gravitationnel de la Lune et du Soleil, les composantes harmoniques comme M2 et S2, et pourquoi les niveaux d'eau observés diffèrent de la prédiction astronomique.",
      blurb:
        "L'analyse harmonique, les composantes qui sous-tendent la marée, et pourquoi la météo fait diverger la réalité.",
      body: [
        {
          p: "Les marées peuvent être prédites des années à l'avance parce qu'elles sont commandées par les mouvements de la Lune et du Soleil, connus avec une grande précision. La marée en un lieu est la réponse de l'océan à ce forçage gravitationnel, façonnée par le trait de côte et la profondeur locaux. La méthode qui transforme l'astronomie en table des marées s'appelle l'analyse harmonique.",
        },
        { h2: 'La marée comme somme de composantes' },
        {
          p: "L'analyse harmonique traite la marée comme la somme de nombreuses ondes simples, chacune appelée composante. Chaque composante a une fréquence fixée par l'astronomie (la façon dont la Terre, la Lune et le Soleil se déplacent les uns par rapport aux autres) tandis que son amplitude et son calage sont déterminés en ajustant les composantes à une période d'observations réelles du niveau de l'eau en ce lieu. Les principales composantes comprennent :",
        },
        {
          list: [
            {
              term: 'M2',
              text: 'la composante lunaire semi-diurne principale, de période 12.42 heures ; généralement la plus grande.',
            },
            {
              term: 'S2',
              text: 'la composante solaire semi-diurne principale, de période 12.00 heures.',
            },
            { term: 'N2', text: "une composante lunaire due à l'orbite elliptique de la Lune." },
            {
              term: 'K1 et O1',
              text: "les principales composantes diurnes, qui rendent une pleine mer du jour plus grande que l'autre en de nombreux endroits.",
            },
          ],
        },
        {
          p: "Le cycle familier vives-eaux/mortes-eaux en découle naturellement : M2 et S2 ont des périodes légèrement différentes, si bien qu'elles se déphasent et se rephasent, se renforçant tous les 14.8 jours pour produire les vives-eaux et se compensant en partie pour produire les mortes-eaux.",
        },
        { h2: "Prédire l'avenir" },
        {
          p: "Une fois connues l'amplitude et la phase de chaque composante pour un lieu, prédire la marée revient simplement à évaluer la somme à n'importe quel instant futur. Les fréquences astronomiques ne changent jamais, de sorte que le calcul est déterministe et peut être mené des décennies à l'avance.",
        },
        { h2: 'Pourquoi la marée réelle diffère' },
        {
          p: "Une prédiction n'est que la marée astronomique. Elle n'inclut pas la météo. Des vents forts, une pression atmosphérique basse ou haute, de fortes pluies et le débit des rivières peuvent élever ou abaisser le niveau réel de l'eau : une surcote de tempête peut ajouter un mètre ou plus au-dessus de la prédiction, tandis qu'une haute pression et des vents de terre peuvent maintenir l'eau bien en dessous. Les prédictions sont donc un excellent guide pour le moment et l'ampleur de la marée, mais le niveau observé le jour même peut différer.",
        },
      ],
    },
  },
}
